// Frontend mirror of server/src/types/risk.ts.
// Keep in sync with the backend RiskAnalysis schema.

export type RiskLevel = 'low' | 'medium' | 'high';

/** PT-BR display labels for risk levels. Use these when rendering UI text. */
export const LEVEL_LABELS: Record<RiskLevel, string> = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
};

export type RiskDimensionId =
  | 'thematic_coherence'
  | 'rights_impact'
  | 'legislative_transparency'
  | 'legal_risk'
  | 'regulatory_complexity';

export interface RiskExcerpt {
  excerpt: string;
  sentenceIndex: number | null;
  charStart: number | null;
  charEnd: number | null;
}

export interface RiskDimension {
  score: number;
  level: RiskLevel;
  indicators: string[];
  excerpts: RiskExcerpt[];
  rationale: string;
}

export interface RiskClassification {
  temas: string[];
  publicoAlvo: string[];
  temperatura: 'baixa' | 'média' | 'alta' | null;
  temperaturaMotivo: string | null;
  impactoEconomico: 'baixo' | 'médio' | 'alto' | null;
  alcance: 'local' | 'regional' | 'nacional' | null;
}

export type RiskProvider =
  | 'anthropic-haiku'
  | 'anthropic-sonnet'
  | 'google-gemini'
  | 'heuristic';

export interface RiskAnalysis {
  ialScore: number;
  ialLevel: RiskLevel;
  summary: string;
  summarizedTitle: string | null;
  dimensions: Record<RiskDimensionId, RiskDimension>;
  classification: RiskClassification;
  provider: RiskProvider;
  disclaimer: string;
  sourceHash: string;
  analyzedAt: string;
}

export const ALL_DIMENSIONS: RiskDimensionId[] = [
  'thematic_coherence',
  'rights_impact',
  'legislative_transparency',
  'legal_risk',
  'regulatory_complexity',
];

/**
 * PT-BR labels for the 5 dimensions, used in UI rendering.
 * Display copy lives in PT-BR per the bilingual convention.
 */
export const DIMENSION_LABELS: Record<RiskDimensionId, string> = {
  thematic_coherence: 'Aderência temática',
  rights_impact: 'Impacto em direitos',
  legislative_transparency: 'Transparência legislativa',
  legal_risk: 'Risco jurídico',
  regulatory_complexity: 'Complexidade regulatória',
};

/**
 * Short PT-BR description of each dimension, used in tooltips and legends.
 */
export const DIMENSION_DESCRIPTIONS: Record<RiskDimensionId, string> = {
  thematic_coherence:
    'Quanto o conteúdo se mantém aderente ao objeto declarado no título.',
  rights_impact:
    'Magnitude da restrição, expansão ou criação de direitos.',
  legislative_transparency:
    'Clareza redacional, especificidade e ausência de delegação obscura.',
  legal_risk:
    'Probabilidade de questionamento (vícios formais ou pontos sensíveis).',
  regulatory_complexity:
    'Carga de obrigações, prazos e custo de adaptação para destinatários.',
};
